import { templates } from '@/data/templates';

/**
 * Merges a template preset into the current project draft.
 * Rules:
 * - Primitives (string, number): Template value overrides only if current draft value is falsy.
 * - Booleans: Template value overrides only if current draft value is undefined.
 * - Arrays: Merges unique values from both draft and template.
 * @param {object} template - The template object.
 * @param {object} currentDraft - The current project draft from context.
 * @returns {object} A new draft object with the template applied.
 */
export function applyTemplateToDraft(template, currentDraft) {
  const newDraft = { ...currentDraft };
  const preset = template.preset;

  if (!preset) return newDraft;

  // Add template metadata to the draft
  newDraft.templateId = template.id;
  newDraft.templateName = template.name;

  for (const key in preset) {
    const draftValue = newDraft[key];
    const templateValue = preset[key];

    if (Array.isArray(templateValue)) {
      const existingArray = Array.isArray(draftValue) ? draftValue : [];
      newDraft[key] = [...new Set([...existingArray, ...templateValue])];
    } else if (typeof templateValue === 'boolean') {
      if (draftValue === undefined) {
        newDraft[key] = templateValue;
      }
    } else {
      if (!draftValue) {
        newDraft[key] = templateValue;
      }
    }
  }
  return newDraft;
}


/**
 * Finds a template by its ID.
 * @param {string} id - The ID of the template.
 * @returns {object|undefined} The template object or undefined if not found.
 */
export function getTemplateById(id) {
  return templates.find(template => template.id === id);
}

/**
 * Lists all templates that belong to a specific category.
 * @param {string} categoryId - The ID of the category.
 * @returns {Array<object>} An array of matching template objects.
 */
export function listTemplatesByCategory(categoryId) {
  return templates.filter(template => template.applyTo === categoryId);
}